import { createContext, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Each journal article lives at exactly one URL per language.
// EN slug <-> NL slug, used for the language switcher and hreflang pairs.
export const JOURNAL_SLUG_MAP = {
  "birth-as-a-rite-of-passage-reclaiming-the-power-of-birth": "geboorte-als-overgangsritueel-de-kracht-van-geboorte-herwaarderen",
  "menopause-a-time-of-change-and-becoming": "een-tijd-van-verandering-en-worden",
  "the-wisdom-of-thresholds": "de-wijsheid-van-drempels",
  "postpartum-recovery-gentle-support-for-new-mothers": "postpartum-herstel-zachte-ondersteuning-voor-nieuwe-moeders",
  "understanding-dutch-maternity-care-from-pregnancy-to-the-kraamweek": "nederlandse-geboortezorg-begrijpen-van-zwangerschap-tot-de-kraamweek",
};
const NL_TO_EN = Object.fromEntries(Object.entries(JOURNAL_SLUG_MAP).map(([en, nl]) => [nl, en]));

// Paths that only exist in English (utilities, admin, payments)
export const EN_ONLY_PATHS = ["/admin", "/payment", "/unsubscribe"];

export const stripPrefix = (pathname) =>
  pathname === "/nl" ? "/" : pathname.startsWith("/nl/") ? pathname.slice(3) : pathname;

export const localizedPath = (pathname, lang) => {
  const base = stripPrefix(pathname);
  return lang === "nl" ? (base === "/" ? "/nl" : `/nl${base}`) : base;
};

// The same page in the other language (journal articles map to their translated slug)
export const alternatePath = (pathname, target) => {
  const base = stripPrefix(pathname);
  if (target === "nl" && EN_ONLY_PATHS.some((p) => base === p || base.startsWith(`${p}/`))) return "/nl";
  const m = base.match(/^\/journal\/(.+)$/);
  let mapped = base;
  if (m) {
    const slug = m[1];
    const other = target === "nl" ? JOURNAL_SLUG_MAP[slug] : NL_TO_EN[slug];
    if (other) {
      mapped = `/journal/${other}`;
    } else if ((target === "nl" ? NL_TO_EN[slug] : JOURNAL_SLUG_MAP[slug])) {
      mapped = base; // slug is already in the target language — self-reference
    } else {
      mapped = "/journal";
    }
  }
  return localizedPath(mapped, target);
};

const LangContext = createContext({ lang: "en", setLang: () => {}, switchLang: () => {} });

export const LanguageProvider = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const lang = pathname === "/nl" || pathname.startsWith("/nl/") ? "nl" : "en";

  useEffect(() => {
    localStorage.setItem("wd-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const switchLang = (target) => {
    if (target !== lang) navigate(alternatePath(pathname, target));
  };

  return <LangContext.Provider value={{ lang, setLang: switchLang, switchLang }}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
